/** Scene-level manager for Officer buff auras — queries active buffs to compute damage and speed multipliers. */
import type { Enemy } from '../entities/Enemy';
import { EnemyBuffSkillEffect } from '../skills/enemy/EnemyBuffSkillEffect';
import { distance } from '../utils/Math';

/**
 * Tracks active Officer buff auras and provides multiplier queries for combat and movement.
 * No config mutation — multipliers are computed on demand from active buff skills.
 */
export class EnemyBuffManager {
  private enemies: readonly Enemy[] = [];

  /** Set the enemy roster reference. Called once from GameScene after spawning. */
  setEnemies(enemies: readonly Enemy[]): void {
    this.enemies = enemies;
  }

  /** Get the highest active damage multiplier affecting the given enemy. Returns 1.0 if no buff applies. */
  getDamageMultiplier(target: Enemy): number {
    let best = 1.0;
    for (const enemy of this.enemies) {
      if (enemy === target) continue;
      const buffSkill = this.getActiveBuffSkill(enemy);
      if (!buffSkill) continue;
      const dist = distance(buffSkill.originX, buffSkill.originY, target.sprite.x, target.sprite.y);
      if (dist <= buffSkill.buffRadius) {
        best = Math.max(best, buffSkill.buffDamageMultiplier);
      }
    }
    return best;
  }

  /** Get the highest active speed multiplier affecting the given enemy. Returns 1.0 if no buff applies. */
  getSpeedMultiplier(target: Enemy): number {
    let best = 1.0;
    for (const enemy of this.enemies) {
      if (enemy === target) continue;
      const buffSkill = this.getActiveBuffSkill(enemy);
      if (!buffSkill) continue;
      const dist = distance(buffSkill.originX, buffSkill.originY, target.sprite.x, target.sprite.y);
      if (dist <= buffSkill.buffRadius) {
        best = Math.max(best, buffSkill.buffSpeedMultiplier);
      }
    }
    return best;
  }

  /** Find the active buff skill on an enemy, if any. */
  private getActiveBuffSkill(enemy: Enemy): EnemyBuffSkillEffect | null {
    const skill = enemy.ai.activeSkill;
    if (skill instanceof EnemyBuffSkillEffect && skill.isBuffActive()) {
      return skill;
    }
    return null;
  }
}
